import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Download, Loader2, Maximize2, Minimize2, Pencil, Pin, PinOff, Sparkles, Square, X } from 'lucide-react';

import { downloadChartAsExcel, hasChartExportData } from '../../../pages/dslAgentTest/chartExport';
import {
  CHART_PERIODS,
  type ChartPeriod,
} from '../../../pages/dslAgentTest/chartPeriod';
import { DslResultChart } from '../../../pages/dslAgentTest/DslResultChart';
import {
  DEFAULT_LINE_STYLE,
  DEFAULT_LINE_WIDTH,
  pickTagColor,
} from '../../../pages/dslAgentTest/colorPalette';
import { runDslTags } from '../../../pages/dslAgentTest/tagExecutor';
import type { DslTag, TagExecState } from '../../../pages/dslAgentTest/types';
import { generateDsl, type DslErrorPayload } from '../../../services/DslService';
import { WIDGET_DRAG_HANDLE_CLASS } from '../../../components/grid-dashboard';

import {
  ChartLegend,
  MONITOR_DSL_DRAG_MIME,
  type MonitorDslDragPayload,
} from './ChartLegend';
import { useMarketMonitorLiveContext } from '../live/MarketMonitorLiveContext';
import styles from './MonitorDashboard.module.css';
import { newId } from './types';

const MAX_CHART_TITLE_LEN = 80;

const MAX_TAGS_PER_CHART = 10;

const formatExecuteError = (payload: DslErrorPayload): string => {
  const location = typeof payload.offset === 'number' ? ` at offset ${payload.offset}` : '';
  const hint = payload.hint ? `\nHint: ${payload.hint}` : '';
  return `[${payload.category} / ${payload.code}] ${payload.message}${location}${hint}`;
};

const toErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error) return error.message;
  return fallback;
};

export interface MonitorChartProps {
  chartIndex: number;
  title: string;
  period: ChartPeriod;
  tags: DslTag[];
  pinned: boolean;
  onTitleChange: (title: string) => void;
  onPeriodChange: (period: ChartPeriod) => void;
  onTagsChange: (tags: DslTag[]) => void;
  onTogglePin: () => void;
  onRemove: () => void;
}

export const MonitorChart: React.FC<MonitorChartProps> = ({
  chartIndex,
  title,
  period,
  tags,
  pinned,
  onTitleChange,
  onPeriodChange,
  onTagsChange,
  onTogglePin,
  onRemove,
}) => {
  const chartInstanceId = useId();

  const [request, setRequest] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const generateAbortRef = useRef<AbortController | null>(null);
  const [bankNotice, setBankNotice] = useState<string | null>(null);

  const [execState, setExecState] = useState<Record<string, TagExecState>>({});
  const [hiddenTagIds, setHiddenTagIds] = useState<Set<string>>(() => new Set());
  const [resetToken, setResetToken] = useState<number>(0);
  const [isMaximized, setIsMaximized] = useState<boolean>(false);
  const [isDropTarget, setIsDropTarget] = useState<boolean>(false);
  const [isPromptDropTarget, setIsPromptDropTarget] = useState<boolean>(false);
  const promptInputRef = useRef<HTMLTextAreaElement | null>(null);

  const [isEditingTitle, setIsEditingTitle] = useState<boolean>(false);
  const [titleDraft, setTitleDraft] = useState<string>(title);
  const [titleShake, setTitleShake] = useState<boolean>(false);
  const titleInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!isEditingTitle) setTitleDraft(title);
  }, [title, isEditingTitle]);

  useEffect(() => {
    if (!isEditingTitle) return;
    const node = titleInputRef.current;
    if (!node) return;
    node.focus();
    node.select();
  }, [isEditingTitle]);

  const beginEditTitle = useCallback(() => {
    setTitleDraft(title);
    setIsEditingTitle(true);
  }, [title]);

  const cancelEditTitle = useCallback(() => {
    setTitleDraft(title);
    setIsEditingTitle(false);
  }, [title]);

  const triggerInvalidShake = useCallback(() => {
    setTitleShake(true);
    window.setTimeout(() => setTitleShake(false), 220);
  }, []);

  const commitEditTitle = useCallback(() => {
    const trimmed = titleDraft.trim();
    if (!trimmed) {
      triggerInvalidShake();
      cancelEditTitle();
      return;
    }
    if (trimmed === title) {
      setIsEditingTitle(false);
      return;
    }
    onTitleChange(trimmed.slice(0, MAX_CHART_TITLE_LEN));
    setIsEditingTitle(false);
  }, [titleDraft, title, onTitleChange, cancelEditTitle, triggerInvalidShake]);

  const handleTitleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        commitEditTitle();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        cancelEditTitle();
      }
    },
    [commitEditTitle, cancelEditTitle],
  );

  const toggleMaximized = useCallback(() => {
    setIsMaximized((prev) => !prev);
    setResetToken((n) => n + 1);
  }, []);

  useEffect(() => {
    if (!isMaximized) return undefined;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMaximized(false);
        setResetToken((n) => n + 1);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isMaximized]);

  const live = useMarketMonitorLiveContext();
  const liveTagStates = live.tagStates;

  const executeTag = useCallback(async (tag: DslTag) => {
    const results = await runDslTags([tag]);
    const entry = results.get(tag.id);
    if (!entry) return;

    setExecState((prev) => {
      const next = { ...prev };
      if (entry.kind === 'error') {
        next[tag.id] = { status: 'error', result: null, error: entry.message };
      } else if (entry.response.error) {
        next[tag.id] = {
          status: 'error',
          result: null,
          error: formatExecuteError(entry.response.error),
        };
      } else {
        next[tag.id] = {
          status: 'ok',
          result: entry.response.result,
          error: null,
        };
      }
      return next;
    });
    setResetToken((n) => n + 1);
  }, []);

  const knownTagIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const known = knownTagIdsRef.current;
    const currentIds = new Set(tags.map((t) => t.id));

    const newTags = tags.filter((t) => !known.has(t.id));
    const removedIds: string[] = [];
    known.forEach((id) => {
      if (!currentIds.has(id)) removedIds.push(id);
    });

    if (newTags.length === 0 && removedIds.length === 0) return;

    if (newTags.length > 0) {
      setExecState((prev) => {
        const next = { ...prev };
        for (const t of newTags) {
          next[t.id] = { status: 'loading', result: null, error: null };
        }
        return next;
      });
      if (live.enabled && live.client) {
        for (const t of newTags) live.client.subscribe(t.id, t.dsl);
      } else {
        for (const t of newTags) void executeTag(t);
      }
    }

    if (removedIds.length > 0) {
      setExecState((prev) => {
        let mutated = false;
        const next = { ...prev };
        for (const id of removedIds) {
          if (id in next) {
            delete next[id];
            mutated = true;
          }
        }
        return mutated ? next : prev;
      });
      setHiddenTagIds((prev) => {
        let mutated = false;
        const next = new Set<string>();
        prev.forEach((id) => {
          if (currentIds.has(id)) next.add(id);
          else mutated = true;
        });
        return mutated ? next : prev;
      });
      if (live.enabled && live.client) {
        for (const id of removedIds) {
          live.client.unsubscribe(id);
          live.resetTagState(id);
        }
      }
      setBankNotice(null);
    }

    knownTagIdsRef.current = currentIds;
  }, [tags, executeTag, live.enabled, live.client, live.resetTagState]);

  // When live mode is on, fold per-tag updates from the WS into execState.
  useEffect(() => {
    if (!live.enabled) return;
    setExecState((prev) => {
      let mutated = false;
      const next = { ...prev };
      for (const tag of tags) {
        const liveEntry = liveTagStates[tag.id];
        if (!liveEntry) continue;
        if (liveEntry.status === 'live') {
          next[tag.id] = { status: 'ok', result: liveEntry.result, error: null };
          mutated = true;
        } else if (liveEntry.status === 'stale') {
          next[tag.id] = {
            status: 'ok',
            result: liveEntry.result,
            error: null,
          };
          mutated = true;
        } else if (liveEntry.status === 'error' && liveEntry.error) {
          next[tag.id] = {
            status: 'error',
            result: liveEntry.result,
            error: `${liveEntry.error.code}: ${liveEntry.error.message}`,
          };
          mutated = true;
        }
      }
      return mutated ? next : prev;
    });
    setResetToken((n) => n + 1);
  }, [live.enabled, liveTagStates, tags]);

  const addTag = useCallback(
    (rawDsl: string): boolean => {
      const trimmed = rawDsl.trim();
      if (!trimmed) return false;
      if (tags.length >= MAX_TAGS_PER_CHART) {
        setBankNotice(`Chart is full (${MAX_TAGS_PER_CHART} max). Remove a trace to add another.`);
        return false;
      }
      if (tags.some((t) => t.dsl === trimmed)) {
        setBankNotice('DSL already on this chart — remove the existing trace first.');
        return false;
      }

      const tag: DslTag = {
        id: newId('tag'),
        dsl: trimmed,
        color: pickTagColor(tags.length),
        lineStyle: DEFAULT_LINE_STYLE,
        lineWidth: DEFAULT_LINE_WIDTH,
      };
      onTagsChange([...tags, tag]);
      setBankNotice(null);
      return true;
    },
    [tags, onTagsChange],
  );

  const handleGenerate = useCallback(async () => {
    const trimmed = request.trim();
    if (!trimmed || isGenerating) return;

    const controller = new AbortController();
    generateAbortRef.current = controller;
    setIsGenerating(true);
    setGenerateError(null);
    try {
      const generated = await generateDsl(trimmed, controller.signal);
      if (addTag(generated)) {
        setRequest('');
      }
    } catch (caught: unknown) {
      if (caught instanceof Error && caught.name === 'AbortError') return;
      setGenerateError(toErrorMessage(caught, 'Failed to generate DSL expression.'));
    } finally {
      generateAbortRef.current = null;
      setIsGenerating(false);
    }
  }, [request, isGenerating, addTag]);

  const handleStopGenerate = useCallback(() => {
    generateAbortRef.current?.abort();
  }, []);

  const handleRemoveTag = useCallback(
    (id: string) => {
      onTagsChange(tags.filter((t) => t.id !== id));
      setBankNotice(null);
    },
    [tags, onTagsChange],
  );

  const handlePatchTag = useCallback(
    (id: string, patch: Partial<DslTag>) => {
      onTagsChange(tags.map((t) => (t.id === id ? { ...t, ...patch } : t)));
    },
    [tags, onTagsChange],
  );

  const handleToggleVisibility = useCallback((id: string) => {
    setHiddenTagIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const dragHasMonitorPayload = (event: React.DragEvent<HTMLElement>): boolean => {
    const types = event.dataTransfer.types;
    for (let i = 0; i < types.length; i += 1) {
      if (types[i] === MONITOR_DSL_DRAG_MIME) return true;
    }
    return false;
  };

  const handleDragOver = useCallback((event: React.DragEvent<HTMLElement>) => {
    if (!dragHasMonitorPayload(event)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
    setIsDropTarget(true);
  }, []);

  const handleDragLeave = useCallback((event: React.DragEvent<HTMLElement>) => {
    if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
    setIsDropTarget(false);
  }, []);

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLElement>) => {
      const raw = event.dataTransfer.getData(MONITOR_DSL_DRAG_MIME);
      if (!raw) return;
      event.preventDefault();
      setIsDropTarget(false);

      let payload: MonitorDslDragPayload;
      try {
        payload = JSON.parse(raw) as MonitorDslDragPayload;
      } catch {
        return;
      }
      if (!payload || typeof payload.dsl !== 'string') return;
      if (payload.sourceChartId === chartInstanceId) return;
      addTag(payload.dsl);
    },
    [chartInstanceId, addTag],
  );

  const handlePromptDragOver = useCallback((event: React.DragEvent<HTMLTextAreaElement>) => {
    if (!dragHasMonitorPayload(event)) return;
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = 'copy';
    setIsPromptDropTarget(true);
    setIsDropTarget(false);
  }, []);

  const handlePromptDragLeave = useCallback((event: React.DragEvent<HTMLTextAreaElement>) => {
    if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
    setIsPromptDropTarget(false);
  }, []);

  const handlePromptDrop = useCallback(
    (event: React.DragEvent<HTMLTextAreaElement>) => {
      const raw = event.dataTransfer.getData(MONITOR_DSL_DRAG_MIME);
      if (!raw) return;
      event.preventDefault();
      event.stopPropagation();
      setIsPromptDropTarget(false);

      let payload: MonitorDslDragPayload;
      try {
        payload = JSON.parse(raw) as MonitorDslDragPayload;
      } catch {
        return;
      }
      if (!payload || typeof payload.dsl !== 'string') return;

      const target = promptInputRef.current;
      const insertText = payload.dsl;
      setRequest((prev) => {
        const start = target?.selectionStart ?? prev.length;
        const end = target?.selectionEnd ?? prev.length;
        const next = prev.slice(0, start) + insertText + prev.slice(end);
        if (target) {
          const caret = start + insertText.length;
          requestAnimationFrame(() => {
            target.focus();
            target.setSelectionRange(caret, caret);
          });
        }
        return next;
      });
    },
    [],
  );

  const handlePromptKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (isGenerating) {
        handleStopGenerate();
      } else if (request.trim().length > 0) {
        void handleGenerate();
      }
    }
  };

  const canGenerate = request.trim().length > 0 && !isGenerating;

  const canDownload = useMemo(
    () => hasChartExportData(tags, execState),
    [tags, execState],
  );

  const handleDownload = useCallback(() => {
    downloadChartAsExcel(tags, execState);
  }, [tags, execState]);

  const aggregatedError = useMemo<string | null>(() => {
    const messages: string[] = [];
    if (generateError) messages.push(generateError);
    if (bankNotice) messages.push(bankNotice);
    for (const tag of tags) {
      const state = execState[tag.id];
      if (state?.status === 'error' && state.error) {
        messages.push(`${tag.dsl}: ${state.error}`);
      }
    }
    return messages.length > 0 ? messages.join('\n') : null;
  }, [generateError, bankNotice, tags, execState]);

  return (
    <article
      className={[
        styles.chart,
        isMaximized ? styles.chartMaximized : '',
        isDropTarget ? styles.chartDropTarget : '',
      ]
        .filter(Boolean)
        .join(' ')}
      data-testid="monitor-chart"
      data-chart-index={chartIndex}
      data-maximized={isMaximized || undefined}
      data-drop-target={isDropTarget || undefined}
      onDragOver={handleDragOver}
      onDragEnter={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <header
        className={[
          styles.chartHeader,
          pinned ? styles.chartHeaderPinned : WIDGET_DRAG_HANDLE_CLASS,
        ].join(' ')}
      >
        <div className={styles.chartTitleGroup}>
          {isEditingTitle ? (
            <input
              ref={titleInputRef}
              type="text"
              className={`${styles.chartTitleInput} ${titleShake ? styles.chartTitleShake : ''}`}
              value={titleDraft}
              maxLength={MAX_CHART_TITLE_LEN}
              onChange={(event) => setTitleDraft(event.target.value)}
              onKeyDown={handleTitleKeyDown}
              onBlur={commitEditTitle}
              aria-label={`Rename chart ${chartIndex + 1}`}
              data-testid="monitor-chart-title-input"
            />
          ) : (
            <button
              type="button"
              className={`${styles.chartTitleButton} ${titleShake ? styles.chartTitleShake : ''}`}
              onClick={beginEditTitle}
              aria-label={`Rename chart ${chartIndex + 1}`}
              title="Click to rename"
              data-testid="monitor-chart-title"
            >
              <span className={styles.chartTitle}>{title}</span>
              <Pencil size={11} aria-hidden="true" className={styles.chartTitleEditIcon} />
            </button>
          )}
          <button
            type="button"
            className={styles.chartIconButton}
            onClick={handleDownload}
            disabled={!canDownload}
            aria-label={`Download chart ${chartIndex + 1} data as Excel`}
            title="Download Excel"
            data-testid="monitor-chart-download"
          >
            <Download size={14} aria-hidden="true" />
          </button>
        </div>
        <div className={styles.chartHeaderActions}>
          <button
            type="button"
            className={[styles.chartIconButton, pinned ? styles.chartIconButtonActive : '']
              .filter(Boolean)
              .join(' ')}
            onClick={onTogglePin}
            aria-label={pinned ? `Unpin chart ${chartIndex + 1}` : `Pin chart ${chartIndex + 1}`}
            aria-pressed={pinned}
            title={pinned ? 'Unpin — allow moving and resizing' : 'Pin — lock position and size'}
            data-testid="monitor-chart-toggle-pin"
          >
            {pinned ? (
              <PinOff size={14} aria-hidden="true" />
            ) : (
              <Pin size={14} aria-hidden="true" />
            )}
          </button>
          <button
            type="button"
            className={styles.chartIconButton}
            onClick={toggleMaximized}
            aria-label={isMaximized ? `Minimize chart ${chartIndex + 1}` : `Maximize chart ${chartIndex + 1}`}
            data-testid="monitor-chart-toggle-maximize"
          >
            {isMaximized ? (
              <Minimize2 size={14} aria-hidden="true" />
            ) : (
              <Maximize2 size={14} aria-hidden="true" />
            )}
          </button>
          <button
            type="button"
            className={styles.chartIconButton}
            onClick={onRemove}
            aria-label={`Remove chart ${chartIndex + 1}`}
            data-testid="monitor-chart-remove"
          >
            <X size={14} aria-hidden="true" />
          </button>
        </div>
      </header>

      <div className={styles.dslBar}>
        <div className={styles.dslRow}>
          <div
            className={[styles.dslInputWrap, isGenerating ? styles.dslInputLoading : '']
              .filter(Boolean)
              .join(' ')}
          >
            <textarea
              ref={promptInputRef}
              className={[styles.dslInput, isPromptDropTarget ? styles.dslInputDropTarget : '']
                .filter(Boolean)
                .join(' ')}
              placeholder="Describe the analytics (e.g. moving average of pnl of SPY 2025)…"
              value={request}
              onChange={(event) => setRequest(event.target.value)}
              onKeyDown={handlePromptKeyDown}
              onDragOver={handlePromptDragOver}
              onDragEnter={handlePromptDragOver}
              onDragLeave={handlePromptDragLeave}
              onDrop={handlePromptDrop}
              disabled={isGenerating}
              rows={1}
              aria-label="Natural language DSL request"
              data-drop-target={isPromptDropTarget || undefined}
              data-testid="monitor-chart-prompt"
            />
          </div>
          <button
            type="button"
            className={isGenerating ? styles.stopButton : styles.primaryButton}
            onClick={isGenerating ? handleStopGenerate : handleGenerate}
            disabled={!isGenerating && !canGenerate}
            title={isGenerating ? 'Stop generation' : 'Generate DSL (Enter)'}
            aria-label={isGenerating ? 'Stop DSL generation' : 'Generate DSL expression'}
            data-testid="monitor-chart-generate"
          >
            {isGenerating ? (
              <Square size={14} aria-hidden="true" />
            ) : (
              <Sparkles size={14} aria-hidden="true" />
            )}
          </button>
        </div>
      </div>

      <div
        className={styles.periodSelector}
        role="radiogroup"
        aria-label={`Chart ${chartIndex + 1} time period`}
        data-testid="monitor-chart-period-selector"
      >
        {CHART_PERIODS.map((option) => {
          const selected = option === period;
          return (
            <button
              key={option}
              type="button"
              className={`${styles.periodButton} ${selected ? styles.periodButtonActive : ''}`}
              role="radio"
              aria-checked={selected}
              onClick={() => onPeriodChange(option)}
              data-testid={`monitor-chart-period-${option}`}
              data-active={selected || undefined}
            >
              {option}
            </button>
          );
        })}
      </div>

      <DslResultChart
        tags={tags}
        execState={execState}
        resetToken={resetToken}
        hiddenTagIds={hiddenTagIds}
        compact
        externalError={aggregatedError}
        viewPeriod={period}
      />

      <ChartLegend
        sourceChartId={chartInstanceId}
        tags={tags}
        execState={execState}
        hiddenTagIds={hiddenTagIds}
        onToggleVisibility={handleToggleVisibility}
        onRemoveTag={handleRemoveTag}
        onPatchTag={handlePatchTag}
      />
    </article>
  );
};
